import React, { useEffect, useState } from 'react';
import { AlertCircle, X, Bell, ArrowDownLeft, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import nkMahnNoticeService from '@/services/nkMahnNoticeService';

/**
 * NkMahnNotice – Hinweis-Komponente für offene NK-Forderungen, Mahnstufen
 * und offene NK-Guthaben/Verbindlichkeiten.
 * Modi:
 *   'sidebar' – kompakte Zählerzeile (aufklappbar per Klick, standardmäßig geschlossen)
 *   'popup'   – zentrales Hinweisfenster beim Seitenaufruf (max. 1x pro Tag, schließbar)
 *   'inline'  – einklappbarer Banner im Arbeitsbereich (standardmäßig geschlossen)
 *
 * Read-only: Es wird nichts gebucht, verrechnet, gemahnt oder kommuniziert.
 * Name→PDF: Empfänger-/Mieternamen mit PDF-Symbol sind anklickbar und öffnen
 * die zugehörige NK-Abrechnung (bestehender, authentifizierter Dokument-Pfad).
 */
export default function NkMahnNotice({ mode = 'sidebar' }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    nkMahnNoticeService.getSummary()
      .then((d) => { if (alive) setData(d); })
      .catch(() => { /* Hinweis bleibt still – kein Fehler-Popup */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (mode !== 'popup' || !data) return;
    const key = 'nkMahnNotice_lastShown';
    const today = new Date().toISOString().slice(0, 10);
    const dismissed = typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem(key) : null;
    const hasWork = (data.nk_open_count || 0) + (data.dunning_2plus_count || 0) + (data.nk_credit_open_count || 0) > 0;
    if (hasWork && dismissed !== today) setOpen(true);
  }, [mode, data]);

  if (!data) return null;
  const nkCount = data.nk_open_count || 0;
  const dunnCount = data.dunning_2plus_count || 0;
  const creditCount = data.nk_credit_open_count || 0;
  const creditSum = Number(data.nk_credit_open_sum || 0);
  const creditItems = data.nk_credit_items || [];
  const nkItems = data.nk_claims || [];
  const total = nkCount + dunnCount + creditCount;
  const fmt = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

  const dismissToday = () => {
    setOpen(false);
    try {
      window.localStorage.setItem('nkMahnNotice_lastShown', new Date().toISOString().slice(0, 10));
    } catch (e) { /* ignore */ }
  };

  const openRentTracking = () => {
    dismissToday();
    window.location.href = '/mieteingangskontrolle';
  };

  // Name→PDF: bestehende authentifizierte Dokument-Route; PDF öffnet im neuen Tab.
  const openPdf = async (item, key) => {
    if (!item || !item.document_id || !item.organization_id || !item.property_id) return;
    setPdfBusy(key);
    try {
      const params = new URLSearchParams({
        property_id: String(item.property_id),
        document_id: String(item.document_id),
      });
      // Auth-Header wie bei notice-summary (Proxy leitet organization_id aus Session weiter)
      const { data: { session } } = await nkMahnNoticeService.getSession();
      const resp = await fetch(`/api/rent/contract-history/document?${params.toString()}`, {
        credentials: 'include',
        headers: {
          'x-supabase-access-token': session?.access_token || '',
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) throw new Error('document_unavailable');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      window.alert('Dokument derzeit nicht verfügbar.');
    } finally {
      setPdfBusy(null);
    }
  };

  // Anzeige-Label: "Name · EH xx" (nur was vorhanden ist)
  const itemLabel = (item, fallback) => {
    const name = item.recipient_name || fallback || 'Mieter';
    const unit = item.unit_label || '';
    return unit ? `${name} · ${unit}` : name;
  };

  // Klickbare Namen mit PDF-Symbol (nur wenn Dokument verknüpft)
  const PdfName = ({ item, name, k }) => {
    const hasDoc = Boolean(item && item.document_id && item.organization_id);
    return (
      <button
        type="button"
        disabled={!hasDoc || pdfBusy === k}
        onClick={() => hasDoc && openPdf(item, k)}
        title={hasDoc ? 'NK-Abrechnung als PDF öffnen' : 'Kein Dokument hinterlegt'}
        className={`inline-flex items-center gap-1 rounded px-1 py-0.5 text-left ${hasDoc ? 'text-emerald-800 underline decoration-emerald-400 underline-offset-2 hover:bg-emerald-100 dark:text-emerald-300' : 'cursor-default opacity-70'}`}
      >
        {hasDoc && <FileText className="h-3 w-3 shrink-0" />}
        <span>{name}</span>
        {pdfBusy === k && <span className="text-[10px] opacity-70">…</span>}
      </button>
    );
  };

  if (mode === 'sidebar') {
    if (total === 0) return null;
    return (
      <div className="mx-3 mb-2 rounded-lg border border-amber-200/70 bg-amber-50/70 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-100">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-between gap-1.5 px-3 py-1.5 font-semibold hover:bg-amber-100/60 dark:hover:bg-amber-900/30"
        >
          <span className="flex items-center gap-1.5">
            <Bell className="h-3.5 w-3.5" /> Hinweise ({total})
          </span>
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        {expanded && (
          <div className="space-y-0.5 border-t border-amber-200/60 px-3 py-1.5 dark:border-amber-900/30">
            <div className={nkCount ? 'font-medium' : 'opacity-60'}>
              NK-Forderungen offen: {nkCount}
            </div>
            <div className={dunnCount ? 'font-medium' : 'opacity-60'}>
              Mahnstufe 2+: {dunnCount}
            </div>
            {creditCount > 0 && (
              <div className="flex items-center gap-1 font-medium text-emerald-800 dark:text-emerald-300">
                <ArrowDownLeft className="h-3.5 w-3.5" />
                NK-Guthaben offen: {creditCount} · {fmt(creditSum)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (mode === 'inline') {
    if (total === 0) return null;
    return (
      <div className="mb-3 rounded-lg border border-amber-200/70 bg-amber-50/70 text-xs text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-100">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-amber-100/60 dark:hover:bg-amber-900/30"
        >
          <span className="flex items-center gap-2">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            <span>
              <span className="font-semibold">{total} offene Prüfungen</span>
              <span className="ml-1.5 opacity-80">
                ({[nkCount ? `${nkCount} NK-Forderungen` : null, dunnCount ? `${dunnCount} Mahnstufe 2+` : null, creditCount ? `${creditCount} NK-Guthaben` : null].filter(Boolean).join(' · ')})
              </span>
            </span>
          </span>
          {expanded ? <ChevronUp className="h-3.5 w-3.5 shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0" />}
        </button>
        {expanded && (
          <div className="space-y-2 border-t border-amber-200/60 px-3 py-2 dark:border-amber-900/30">
            {nkCount > 0 && (
              <div className="text-sm">
                <div className="font-medium">NK-Forderungen prüfen: {nkCount} offen</div>
                <div className="mt-1 space-y-0.5 text-xs">
                  {nkItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-2">
                      <PdfName item={item} k={`nk-${item.id}`} name={itemLabel(item)} />
                      <span className="opacity-80">{fmt(item.remaining_amount)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-1 text-xs opacity-80">Sind die Zahlungen der Nebenkostenabrechnungen eingegangen?</div>
              </div>
            )}
            {dunnCount > 0 && (
              <div className="text-sm">
                <div className="font-medium">Mahn-Check: {dunnCount} Mieter auf Mahnstufe 2+</div>
                <div className="text-xs opacity-80">Mahnstatus und Zahlungsverhalten prüfen.</div>
              </div>
            )}
            {creditCount > 0 && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-sm dark:border-emerald-900/50 dark:bg-emerald-950/20">
                <div className="flex items-center gap-1.5 font-medium text-emerald-900 dark:text-emerald-200">
                  <ArrowDownLeft className="h-3.5 w-3.5" />
                  NK-Guthaben offen: {creditCount} · {fmt(creditSum)}
                </div>
                <div className="mt-1 space-y-0.5 text-xs text-emerald-800 dark:text-emerald-300">
                  {creditItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-2">
                      <PdfName item={item} k={`cr-${item.id}`} name={itemLabel(item)} />
                      <span className="font-medium">+{fmt(item.credit_amount)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-1 text-xs opacity-75">Auszahlung oder Verrechnung entscheiden – es wird nichts automatisch ausgezahlt oder verrechnet.</div>
              </div>
            )}
            <div className="text-[11px] opacity-60">Es wird nichts automatisch verrechnet, gemahnt oder kommuniziert.</div>
          </div>
        )}
      </div>
    );
  }

  // mode === 'popup'
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[999] flex items-start justify-center bg-black/30 p-4 pt-24">
      <div className="w-full max-w-lg rounded-xl border border-amber-200 bg-white p-5 shadow-2xl dark:border-amber-900/50 dark:bg-slate-900">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Prüfen empfohlen</h2>
          </div>
          <button onClick={dismissToday} className="text-slate-400 hover:text-slate-600" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
          {nkCount > 0 && (
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 text-amber-600" />
              <div>
                <div className="font-medium">NK-Forderungen prüfen: {nkCount} offen</div>
                <div className="text-xs text-slate-500">Sind die Zahlungen der Nebenkostenabrechnungen eingegangen?</div>
              </div>
            </div>
          )}
          {dunnCount > 0 && (
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 text-amber-600" />
              <div>
                <div className="font-medium">Mahn-Check: {dunnCount} Mieter auf Mahnstufe 2+</div>
                <div className="text-xs text-slate-500">Mahnstatus und Zahlungsverhalten prüfen.</div>
              </div>
            </div>
          )}
          {creditCount > 0 && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 dark:border-emerald-900/50 dark:bg-emerald-950/20">
              <div className="flex items-start gap-2">
                <ArrowDownLeft className="mt-0.5 h-4 w-4 text-emerald-600" />
                <div className="w-full">
                  <div className="font-medium text-emerald-900 dark:text-emerald-200">
                    NK-Guthaben prüfen: {creditCount} offen · {fmt(creditSum)}
                  </div>
                  <div className="mt-1 space-y-1 text-xs text-emerald-800 dark:text-emerald-300">
                    {creditItems.map((item) => (
                      <div key={item.id} className="flex items-center justify-between gap-2 rounded bg-white/60 px-2 py-1 dark:bg-black/20">
                        <PdfName item={item} k={`pop-${item.id}`} name={itemLabel(item)} />
                        <span className="font-medium">+{fmt(item.credit_amount)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-1.5 text-xs text-slate-500">Rückzahlungen an Mieter – bitte Auszahlung oder Verrechnung entscheiden. Name klicken öffnet die NK-Abrechnung als PDF.</div>
                </div>
              </div>
            </div>
          )}
          <div className="text-xs text-slate-400">Es wird nichts automatisch verrechnet, gemahnt oder kommuniziert.</div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={openRentTracking} className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700">
            Zur Mieteingangskontrolle
          </button>
          <button onClick={dismissToday} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300">
            Heute nicht mehr anzeigen
          </button>
        </div>
      </div>
    </div>
  );
}
